import { useContent } from 'contexts/Content/Content';
import { getRouteSeo, getUrl } from 'helpers';
import Head from 'next/head';
import { useRouter } from 'next/router';

function normalizeRoutePath(asPath: string, appPathname: string) {
    const appPath = appPathname.replace(/\/$/, '');
    const sanitizedAsPath = (asPath || '/').split(/[?#]/)[0] || '/';
    let routePath = sanitizedAsPath.startsWith('/') ? sanitizedAsPath : `/${sanitizedAsPath}`;

    if (appPath && routePath.startsWith(appPath)) {
        routePath = routePath.slice(appPath.length) || '/';
    }

    if (routePath.length > 1 && routePath.endsWith('/')) {
        routePath = routePath.slice(0, -1);
    }

    return routePath || '/';
}

export default function DocHead() {
    const content = useContent();
    const router = useRouter();

    const {
        canonicalUrl,
        documentDescription,
        documentTitle,
        favicon,
        faq,
        siteName,
        socialDescription,
        socialImage,
        socialImageAlt,
        socialTitle,
    } = content.general;

    const routePath = normalizeRoutePath(router.asPath, new URL(canonicalUrl).pathname);
    const routeSeo = getRouteSeo(routePath);
    const isLandingRoute = routePath === '/';
    const isIndexableRoute = routeSeo.robots.startsWith('index');
    const robotsContent = isIndexableRoute ? 'index,follow,max-image-preview:large' : routeSeo.robots;
    const shareDescription = isLandingRoute ? socialDescription || documentDescription : routeSeo.description;
    const shareTitle = isLandingRoute ? socialTitle || documentTitle : routeSeo.title;
    const shareImage = isLandingRoute ? socialImage : routeSeo.imageUrl;
    const shareImageAlt = isLandingRoute ? socialImageAlt : routeSeo.imageAlt;
    const pageTitle = isLandingRoute ? documentTitle : routeSeo.title;
    const pageDescription = isLandingRoute ? documentDescription : routeSeo.description;
    const webApplicationStructuredData = {
        '@context': 'https://schema.org',
        '@type': 'WebApplication',
        name: 'Innovation Accounting Simulation',
        applicationCategory: 'EducationalApplication',
        description: documentDescription,
        isAccessibleForFree: true,
        offers: {
            '@type': 'Offer',
            price: 0,
            priceCurrency: 'USD',
        },
        operatingSystem: 'Any',
        featureList: [
            'Food truck business simulation',
            'Revenue and ROI prediction practice',
            'Step-by-step financial modeling',
            'Range-based forecasting for uncertainty',
            'Growth loop modeling and refinement',
        ],
        url: canonicalUrl,
    };
    const faqStructuredData = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faq.map(entry => ({
            '@type': 'Question',
            name: entry.question,
            acceptedAnswer: {
                '@type': 'Answer',
                text: entry.answer,
            },
        })),
    };

    return (
        <Head>
            <title>{pageTitle}</title>
            <meta name="description" content={pageDescription} />
            <meta name="robots" content={robotsContent} />
            <meta name="googlebot" content={robotsContent} />
            <link rel="canonical" href={routeSeo.canonicalUrl} />
            <link rel="shortcut icon" href={getUrl(favicon)} />
            <meta property="og:type" content="website" />
            <meta property="og:site_name" content={siteName} />
            <meta property="og:title" content={shareTitle} />
            <meta property="og:description" content={shareDescription} />
            <meta property="og:url" content={routeSeo.canonicalUrl} />
            <meta property="og:image" content={shareImage} />
            <meta property="og:image:alt" content={shareImageAlt} />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={shareTitle} />
            <meta name="twitter:description" content={shareDescription} />
            <meta name="twitter:image" content={shareImage} />
            <meta name="twitter:image:alt" content={shareImageAlt} />
            {isLandingRoute && (
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{ __html: JSON.stringify(webApplicationStructuredData) }}
                />
            )}
            {isLandingRoute && faq.length > 0 && (
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{ __html: JSON.stringify(faqStructuredData) }}
                />
            )}
            {!isLandingRoute && routeSeo.structuredData && (
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{ __html: JSON.stringify(routeSeo.structuredData) }}
                />
            )}
        </Head>
    );
}
